import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ShieldCheck, Send, Activity } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { TerminalShowcase } from "@/components/terminal/TerminalShowcase";
import { TerminalHeroShowcase } from "@/components/terminal/TerminalHeroShowcase";

const PAGE_URL = "https://xauproacademy.com/terminal";
const TITLE = "XAU Terminal™: The Institutional Gold Operating System";
const DESC =
  "One workspace for XAUUSD: Orion AI execution alerts, the 10-pillar XAU MacroLens™ confluence score, StructureLens chart audits and risk tools under a single membership.";

export const Route = createFileRoute("/terminal")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "product" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "XAU Terminal",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: PAGE_URL,
          description: DESC,
          publisher: { "@type": "Organization", name: "XAU Pro Academy" },
        }),
      },
    ],
  }),
  component: TerminalPage,
});

const OPERATIONS = [
  { icon: ShieldCheck, title: "Risk stays in the workflow.", body: "Position sizing and risk context alongside your market analysis.", to: "/position-size-calculator" as const, link: "Position sizing" },
  { icon: Send, title: "A private line to the desk.", body: "Link your Telegram account to receive private 1-to-1 alerts directly from our desk.", to: "/orion" as const, link: "Orion AI desk" },
  { icon: Activity, title: "An accountable record.", body: "Review historical outcomes and follow open strategic position progress.", to: "/track-record" as const, link: "Performance record" },
];

function TerminalPage() {
  const { user } = useAuth();
  const primaryTo = user ? "/dashboard" : "/auth";
  return (
    <Layout>
      <div className="terminal-editorial">
        <section className="relative overflow-hidden border-b border-border" style={{ backgroundImage: "var(--gradient-hero)" }}>
          <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-2 lg:items-center lg:py-20">
          <div className="min-w-0">
            <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.16em] text-gold"><span className="h-px w-8 bg-gold" /> XAU Pro Academy / Gold operating system</div>
            <h1 className="mt-6 font-news-heading text-5xl font-medium leading-tight tracking-normal sm:text-6xl lg:text-7xl">XAU Terminal<span className="align-top text-xl sm:text-2xl">™</span></h1>
            <p className="mt-5 max-w-lg font-news-heading text-xl leading-relaxed text-foreground sm:text-2xl">Your gold market.<br />One connected workspace.</p>
            <p className="mt-5 max-w-md text-sm leading-7 text-muted-foreground">Macro confluence, chart structure, the live desk and risk, combined into one directive. Every module below lives inside the terminal.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" variant="gold"><Link to={primaryTo}>{user ? "Open XAU Terminal™" : "Create free account"}<ArrowRight /></Link></Button>
              <Button asChild size="lg" variant="outline" className="bg-background/40"><Link to="/pricing">View membership</Link></Button>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-7 gap-y-3 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground"><span>01 / Observe</span><span>02 / Qualify</span><span>03 / Manage risk</span></div>
          </div>
          <TerminalHeroShowcase />
          </div>
        </section>
        <TerminalShowcase />
        <section className="border-b border-border">
          <div className="mx-auto grid max-w-6xl gap-9 px-5 py-14 sm:px-8 md:grid-cols-3 lg:py-16">
            {OPERATIONS.map(item => <div key={item.title} className="min-w-0"><item.icon className="h-5 w-5 text-gold" /><h2 className="mt-5 font-news-heading text-lg font-medium tracking-normal">{item.title}</h2><p className="mt-3 text-sm leading-6 text-muted-foreground">{item.body}</p><Button asChild variant="link" className="mt-4 h-auto p-0"><Link to={item.to}>{item.link}<ArrowRight /></Link></Button></div>)}
          </div>
        </section>
        <section>
          <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-20">
            <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
              <div><div className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold">Your workspace awaits</div><h2 className="mt-4 font-news-heading text-3xl font-medium tracking-normal sm:text-4xl">Start with the market. Not a subscription.</h2><p className="mt-5 max-w-xl text-sm leading-7 text-muted-foreground">Explore the terminal with a free account. View gold market context, receive two full showcase setups per month on Telegram and run a weekly StructureLens audit.</p></div>
              <Button asChild size="lg" variant="gold" className="justify-self-start"><Link to={primaryTo}>{user ? "Open XAU Terminal™" : "Create free account"}<ArrowRight /></Link></Button>
            </div>
            <p className="mt-12 border-t border-border pt-6 text-xs leading-5 text-muted-foreground">Trading involves substantial risk. Historical and live performance do not guarantee future results. Illustrative analysis previews are not live market readings.</p>
          </div>
        </section>
      </div>
    </Layout>
  );
}
